import { memo, useState, useMemo, useEffect } from "react";
import { useGetDepartmentByIdQuery } from "../../../../service/department-service";
import { useSupervisorAssignStepMutation } from "../../../../service/customersupport-service";
import { StepInstanceResponse } from "../../../../model/workflows/step-instance";
import Button from "../../../../common/components/xepp/button/Button";
import CommonLoadingSpinner from "../../../../common/components/loading-spinner/CommonLoadingSpinnerForPage";
import { toast } from "react-toastify";
import styles from "./AssignStepModal.module.scss";

type Props = {
	isOpen: boolean;
	onClose: () => void;
	onSuccess: () => void;
	stepInstance: StepInstanceResponse;
	departmentId: string;
	mode: 'assign' | 'reassign';
};

const AssignStepModal = memo((props: Props) => {
	const { isOpen, onClose, onSuccess, stepInstance, departmentId, mode } = props;
	const { data: department, isLoading, isError } = useGetDepartmentByIdQuery(departmentId);
	const [assignStep, { isLoading: isAssigning }] = useSupervisorAssignStepMutation();
	const [selectedStaffId, setSelectedStaffId] = useState<string | null>(null);
	const [searchTerm, setSearchTerm] = useState("");

	useEffect(() => {
		if (!isOpen) return;

		if (mode === "reassign" && stepInstance.assignedUserId) {
			setSelectedStaffId(stepInstance.assignedUserId);
			return;
		}

		setSelectedStaffId(null);
	}, [mode, stepInstance.assignedUserId, isOpen]);

	// Filter staff by search term
	const filteredStaff = useMemo(() => {
		if (!department?.staff) return [];
		const searchLower = searchTerm.toLowerCase();
		return department.staff.filter(staff =>
			`${staff.firstName} ${staff.lastName}`.toLowerCase().includes(searchLower)
		);
	}, [department?.staff, searchTerm]);

	const handleStaffSelect = (staffId: string) => {
		setSelectedStaffId(staffId);
	};

	const handleSubmit = async () => {
		if (!selectedStaffId) {
			toast.error("Please select a staff member");
			return;
		}
		if (mode === "reassign" && selectedStaffId === stepInstance.assignedUserId) {
			toast.error("Please select a different staff member");
			return;
		}

		try {
			await assignStep({
				stepInstanceId: stepInstance.id,
				assignToUserId: selectedStaffId,
			}).unwrap();

			toast.success(mode === 'assign'
				? 'Step assigned successfully'
				: 'Step reassigned successfully'
			);
			onSuccess();
			onClose();
		} catch (error: any) {
			toast.error(error?.data?.message || `Failed to ${mode} step. Please try again.`);
		}
	};

	const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
		if (e.target === e.currentTarget) {
			onClose();
		}
	};

	if (!isOpen) return null;

	const requestType = stepInstance.customerServiceRequestInstance?.serviceRequest?.name || "Unknown Request";
	const customerName = `${stepInstance.customer.firstName} ${stepInstance.customer.lastName}`;
	const currentAssigneeName = stepInstance.assignedUser
		? `${stepInstance.assignedUser.firstName} ${stepInstance.assignedUser.lastName}`
		: null;
	const isCurrentAssigneeSelected =
		mode === "reassign" && selectedStaffId === stepInstance.assignedUserId;

	return (
		<div className={styles.backdrop} onClick={handleBackdropClick}>
			<div className={styles.modal}>
				<div className={styles.header}>
					<h2 className={styles.title}>
						{mode === 'assign' ? 'Assign Task' : 'Reassign Task'}
					</h2>
					<button className={styles.closeButton} onClick={onClose}>
						×
					</button>
				</div>

				{isError ? (
					<div className={styles.error}>
						Failed to load department staff. Please try again.
					</div>
				) : (
					<CommonLoadingSpinner loading={isLoading}>
						<div className={styles.content}>
							<div className={styles.stepDetails}>
								<div className={styles.detailRow}>
									<span className={styles.detailLabel}>Request Type:</span>
									<span className={styles.detailValue}>{requestType}</span>
								</div>
								<div className={styles.detailRow}>
									<span className={styles.detailLabel}>Customer:</span>
									<span className={styles.detailValue}>{customerName}</span>
								</div>
								{mode === 'reassign' && currentAssigneeName && (
									<div className={styles.detailRow}>
										<span className={styles.detailLabel}>Current Assignee:</span>
										<span className={styles.detailValue}>{currentAssigneeName}</span>
									</div>
								)}
							</div>

							<h3 className={styles.sectionTitle}>
								{mode === 'assign' ? 'Choose new team member for this task' : 'Select a different team member'}
							</h3>

							<input
								type="text"
								placeholder="Search by name"
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								className={styles.searchInput}
							/>

							{filteredStaff.length === 0 ? (
								<div className={styles.noStaff}>
									{searchTerm ? 'No staff members found matching your search.' : 'No staff members available in this department.'}
								</div>
							) : (
								<div className={styles.staffList}>
									{filteredStaff.map((staff) => {
										const isCurrentAssignee = stepInstance.assignedUserId === staff.id;
										const isSelected = selectedStaffId === staff.id;

										return (
											<div
												key={staff.id}
												className={`${styles.staffItem} ${isSelected ? styles.selected : ''}`}
												onClick={() => handleStaffSelect(staff.id)}
											>
												<div className={`${styles.radioButton} ${isSelected ? styles.selected : ''}`} />
												<span className={styles.staffName}>
													{staff.firstName} {staff.lastName}
												</span>
												{isCurrentAssignee && mode === 'reassign' && (
													<span className={styles.currentAssignee}>Current Assignee</span>
												)}
											</div>
										);
									})}
								</div>
							)}
						</div>

						<div className={styles.actions}>
							<Button
								text="Cancel"
								variant="secondary"
								onClick={onClose}
								disabled={isAssigning}
							/>
							<Button
								text={isAssigning
									? (mode === 'assign' ? 'Assigning...' : 'Reassigning...')
									: (mode === 'assign' ? 'Assign' : 'Reassign')
								}
								variant="primary"
								onClick={handleSubmit}
								disabled={!selectedStaffId || isAssigning || isCurrentAssigneeSelected}
							/>
						</div>
					</CommonLoadingSpinner>
				)}
			</div>
		</div>
	);
});

export default AssignStepModal;
